import * as yup from 'yup';
import type { FormDefinition } from '@gov-design-system-ce/react-form-core';
import {
	getFormDefinition,
	useGovForm,
} from '@gov-design-system-ce/react-form-core';
import {
	rezaSubmitComplaint,
	useAxiosInstance,
	usePoMutation,
} from '@gov-nx/api';
import { useMessageEvents } from '@gov-nx/core/events';
import type {
	ContactDetailsDefinition,
	ContactDetailsFormData,
	PersonCommunicationData,
} from '@gov-nx/core/hooks';
import {
	useContactDetailsDefinition,
	useContactDetailsFormDefinition,
	useCountryCodes,
	usePersonCommunicationData,
	usePersonCommunicationForm,
} from '@gov-nx/core/hooks';
import { useRezaLocale } from '@gov-nx/module/page';
import type { FormInputProps } from '@gov-nx/ui/types';
import type { FormButtonProps } from '@gov-nx/ui/web';
import type { IUseBoolean } from '@gov-nx/utils/common';
import { phoneShapeToString, useBoolean } from '@gov-nx/utils/common';

export interface RezaComplaintsForm extends ContactDetailsFormData {
	complaint: string;
}

interface Fields {
	complaint: FormInputProps;
	submit: FormButtonProps;
	_useEmail: ContactDetailsDefinition[0]['field'];
	email: ContactDetailsDefinition[1]['field'];
	_usePhone: ContactDetailsDefinition[2]['field'];
	phone: ContactDetailsDefinition[3]['field'];
}

export interface ComplaintsForm {
	isModalOpen: IUseBoolean;
	formDefinition: FormDefinition<RezaComplaintsForm>;
	onSubmit: () => void;
	fields: Fields;
	personCommunicationData: PersonCommunicationData;
}

export const useComplaintsForm = (id?: string): ComplaintsForm => {
	const { t } = useRezaLocale();
	const { toastMessageSuccess } = useMessageEvents();
	const { axiosInstance } = useAxiosInstance();
	const countryCodes = useCountryCodes();

	const personCommunicationData = usePersonCommunicationData();
	const contactDetails = useContactDetailsFormDefinition();

	const mutation = usePoMutation({
		mutationFn: rezaSubmitComplaint,
		onSuccess: () => {
			formDefinition.formReset();
			toastMessageSuccess(t('complaints.form.odeslano'));
		},
	});

	const contactDetailsDefinition = useContactDetailsDefinition({
		isDisabled: mutation.isLoading,
		strings: {
			_useEmail: { label: t('complaints.form.email.label') },
			_usePhone: { label: t('complaints.form.phone.label') },
			email: {
				label: '',
				placeholder: t('complaints.form.email.placeholder'),
			},
			phone: {
				label: '',
				placeholder: t('complaints.form.phone.placeholder'),
			},
		},
	});

	const formSchema = yup
		.object({
			...contactDetails.shape,
			complaint: yup
				.string()
				.required(t('complaints.form.pole.complaint.validace.povinne-pole')),
		})
		.required();
	const formMethods = useGovForm<RezaComplaintsForm>({
		formSchema,
		defaultValues: {
			complaint: undefined,
			...contactDetails.defaultValues,
		},
	});

	const formDefinition = getFormDefinition({ formMethods, formSchema });

	usePersonCommunicationForm(formDefinition, {
		countryCodes,
		email: personCommunicationData.emailVerified,
		phone: personCommunicationData.phoneVerified,
	});

	const onSubmit = formDefinition.formMethods.handleSubmit(
		(data) =>
			mutation.mutate(
				{
					axiosInstance,
					payload: {
						popis: data.complaint,
						opravneniKod: id,
						typ: id ? 'NEMA_EXISTOVAT' : 'NEEXISTENCE',
						kontaktniEmail: data._useEmail ? data.email : undefined,
						kontaktniTelefon:
							data._usePhone && data.phone
								? phoneShapeToString(data.phone)
								: undefined,
					},
				},
				{ onSuccess: isModalOpen.setFalse }
			),
		personCommunicationData.onFormValidated
	);

	const fields: Fields = {
		_useEmail: contactDetailsDefinition[0].field,
		email: contactDetailsDefinition[1].field,
		_usePhone: contactDetailsDefinition[2].field,
		phone: contactDetailsDefinition[3].field,
		complaint: {
			field: {
				name: 'complaint',
				isMultiline: true,
				rows: 4,
				isDisabled: mutation.isLoading,
				placeholder: t('complaints.form.pole.complaint.placeholder'),
			},
			label: {
				children: t('complaints.form.pole.complaint.label'),
			},
		},
		submit: {
			children: t('complaints.form.buttons.submit.label'),
			isDisabled: mutation.isLoading,
			isLoading: mutation.isLoading,
			'aria-label': t('complaints.form.buttons.submit.wcag'),
		},
	};
	const isModalOpen = useBoolean();

	return {
		formDefinition,
		onSubmit,
		fields,
		isModalOpen,
		personCommunicationData,
	};
};
